import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme, fonts } from "@/theme";
import { Icon } from "@/components/Icon";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/lib/toast";
import { CATEGORY_ICON, CATEGORY_LABEL, CONTENT_CATEGORIES, contentApi } from "@/data/api";
import { ShopRitzyRa } from "@/components/ShopRitzyRa";
import { Loader } from "@/components/ui";

export default function Content() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const [selected, setSelected] = useState("all");
  const [openIdea, setOpenIdea] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    contentApi
      .list()
      .then((r) => {
        setAllItems(r.items || []);
        setFeatured(r.featured || []);
      })
      .catch(() => {
        setAllItems([]);
        setFeatured([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const items = useMemo(() => allItems.filter((i) => selected === "all" || i.category === selected), [allItems, selected]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.show("Copied ✨");
    } catch {
      toast.show("Couldn't copy — try selecting the text.");
    }
  };
  const download = (item) => {
    if (item.fileUrl) window.open(item.fileUrl, "_blank");
    else toast.show("Full-res file coming soon 🖤");
  };

  const F = fonts;
  const NewBadge = ({ float }) => (
    <span style={{ position: float ? "absolute" : "static", top: 10, right: 10, background: colors.warning, color: colors.onWarning, borderRadius: 999, padding: "3px 8px", fontSize: 10, fontWeight: 800, letterSpacing: 0.5, fontFamily: F.text }}>NEW</span>
  );
  const CatLabel = ({ item }) => <div style={{ color: colors.brandSecondary, fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 2, fontFamily: F.text }}>{CATEGORY_LABEL[item.category]}</div>;
  const Title = ({ children }) => <div style={{ color: colors.onSurface, fontSize: 17, fontWeight: 700, marginTop: 6, fontFamily: F.text }}>{children}</div>;
  const Quote = ({ children }) => <div style={{ background: colors.surfaceTertiary, borderRadius: 12, padding: 12, marginTop: 10, color: colors.onSurface, fontSize: 14, lineHeight: "20px", whiteSpace: "pre-wrap", fontFamily: F.text }}>{children}</div>;
  const Btn = ({ icon, label, onClick, testid }) => <button data-testid={testid} className="pressable" onClick={onClick} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: colors.brand, color: colors.onBrand, border: "none", borderRadius: 12, padding: "12px", marginTop: 14, cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: F.text }}><Icon name={icon} size={16} color={colors.onBrand} />{label}</button>;
  const Ghost = ({ icon, label, onClick, testid }) => <button data-testid={testid} className="pressable" onClick={onClick} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: colors.surface, color: colors.brand, border: `1px solid ${colors.border}`, borderRadius: 12, padding: "12px 16px", marginTop: 14, cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: F.text }}><Icon name={icon} size={16} color={colors.brand} />{label}</button>;

  const Preview = ({ item, height = 140 }) =>
    item.imageUrl ? (
      <img src={item.imageUrl} alt={item.title} style={{ width: "100%", height, borderRadius: 14, objectFit: "cover", background: colors.surfaceTertiary }} />
    ) : (
      <div style={{ height, borderRadius: 14, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, background: `linear-gradient(135deg, ${colors.brand}, ${colors.brandSecondary})` }}>
        <Icon name={CATEGORY_ICON[item.category]} size={38} color={colors.onBrand} />
        <span style={{ color: colors.onBrand, fontSize: 12, opacity: 0.9, fontFamily: F.text }}>Replace with a RitzyRa graphic</span>
      </div>
    );

  const cardStyle = { background: colors.surfaceSecondary, borderRadius: 18, border: `1px solid ${colors.border}`, padding: 16, marginBottom: 16 };

  const renderItem = (item) => {
    const topRow = (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <CatLabel item={item} />
        {item.isNew ? <NewBadge /> : null}
      </div>
    );
    if (item.category === "captions") {
      return (
        <div key={item.id} data-testid={`content-item-${item.id}`} style={cardStyle}>
          {topRow}
          <Title>{item.title}</Title>
          <Quote>{item.caption}</Quote>
          <div style={{ color: colors.muted, fontSize: 12, fontStyle: "italic", marginTop: 8, fontFamily: F.text }}>Make it yours — tweak the wording into your own voice ✨</div>
          <Btn testid={`copy-${item.id}`} icon="copy" label="Copy Caption" onClick={() => copy(item.caption)} />
        </div>
      );
    }
    if (item.category === "ideas") {
      const open = openIdea === item.id;
      return (
        <div key={item.id} data-testid={`content-item-${item.id}`} style={cardStyle}>
          {topRow}
          <Title>{item.title}</Title>
          {open ? <div style={{ color: colors.onSurfaceSecondary, fontSize: 14, lineHeight: "20px", marginTop: 8, whiteSpace: "pre-wrap", fontFamily: F.text }}>{item.idea}</div> : null}
          <Ghost testid={`idea-${item.id}`} icon={open ? "chevron-up" : "eye"} label={open ? "Hide" : "View Idea"} onClick={() => setOpenIdea(open ? null : item.id)} />
        </div>
      );
    }
    if (item.category === "spotlights") {
      const combined = [item.title, item.facts?.length ? `The facts:\n- ${item.facts.join("\n- ")}` : "", item.spiritual?.length ? `Spiritual associations:\n- ${item.spiritual.join("\n- ")}` : "", item.tip ? `Tip: ${item.tip}` : ""].filter(Boolean).join("\n\n");
      return (
        <div key={item.id} data-testid={`content-item-${item.id}`} style={cardStyle}>
          {topRow}
          <Title>{item.title}</Title>
          {item.facts?.length ? <div style={{ color: colors.onSurface, fontSize: 13, fontWeight: 800, marginTop: 12, marginBottom: 4, fontFamily: F.text }}>The facts</div> : null}
          {item.facts?.map((f, i) => <div key={`f${i}`} style={{ color: colors.onSurfaceSecondary, fontSize: 14, lineHeight: "20px", fontFamily: F.text }}>• {f}</div>)}
          {item.spiritual?.length ? <div style={{ color: colors.onSurface, fontSize: 13, fontWeight: 800, marginTop: 12, marginBottom: 4, fontFamily: F.text }}>Spiritual associations</div> : null}
          {item.spiritual?.map((s, i) => <div key={`s${i}`} style={{ color: colors.onSurfaceSecondary, fontSize: 14, lineHeight: "20px", fontFamily: F.text }}>• {s}</div>)}
          {item.tip ? <div style={{ color: colors.muted, fontSize: 13, marginTop: 10, fontFamily: F.text }}>💡 {item.tip}</div> : null}
          <Btn testid={`copy-${item.id}`} icon="copy" label="Copy talking points" onClick={() => copy(combined)} />
        </div>
      );
    }
    if (item.category === "launches") {
      return (
        <div key={item.id} data-testid={`content-item-${item.id}`} style={cardStyle}>
          <div style={{ position: "relative", marginBottom: 12 }}><Preview item={item} />{item.isNew ? <NewBadge float /> : null}</div>
          <CatLabel item={item} />
          <Title>{item.title}</Title>
          {item.description ? <div style={{ color: colors.onSurfaceSecondary, fontSize: 14, lineHeight: "20px", marginTop: 6, fontFamily: F.text }}>{item.description}</div> : null}
          {item.caption ? <Quote>{item.caption}</Quote> : null}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {item.caption ? <Ghost testid={`copy-${item.id}`} icon="copy" label="Copy Caption" onClick={() => copy(item.caption)} /> : null}
            <Ghost testid={`download-${item.id}`} icon="download" label="Download" onClick={() => download(item)} />
          </div>
        </div>
      );
    }
    return (
      <div key={item.id} data-testid={`content-item-${item.id}`} style={cardStyle}>
        <div style={{ position: "relative", marginBottom: 12 }}><Preview item={item} />{item.isNew ? <NewBadge float /> : null}</div>
        <CatLabel item={item} />
        <Title>{item.title}</Title>
        {item.description ? <div style={{ color: colors.onSurfaceSecondary, fontSize: 14, lineHeight: "20px", marginTop: 6, fontFamily: F.text }}>{item.description}</div> : null}
        <Btn testid={`download-${item.id}`} icon="download" label="Download" onClick={() => download(item)} />
      </div>
    );
  };

  return (
    <div className="ritzy-fade">
      <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${colors.divider}` }}>
        <div style={{ color: colors.onSurface, fontSize: 28, fontFamily: fonts.display, fontWeight: 700 }}>Content</div>
        <div style={{ color: colors.onSurface, fontSize: 16, fontWeight: 700, marginTop: 6, fontFamily: fonts.text }}>Your content stash ✨</div>
        <div style={{ color: colors.muted, fontSize: 13, marginTop: 4, lineHeight: "19px", fontFamily: fonts.text }}>Photos, captions, templates and ideas to make sharing RitzyRa easy. Grab what you need, add your own personality and get posting.</div>
        {user?.is_admin ? (
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button data-testid="manage-content" className="pressable" onClick={() => navigate("/admin/content")} style={{ display: "flex", alignItems: "center", gap: 6, background: colors.brand, color: colors.onBrand, border: "none", borderRadius: 10, padding: "9px 14px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: fonts.text }}><Icon name="edit-3" size={14} color={colors.onBrand} />Manage content</button>
            <button data-testid="shop-settings" className="pressable" onClick={() => navigate("/admin/shopify")} style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", color: colors.brand, border: `1px solid ${colors.border}`, borderRadius: 10, padding: "9px 14px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: fonts.text }}><Icon name="shopping-bag" size={14} color={colors.brand} />Shop settings</button>
          </div>
        ) : null}
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div style={{ paddingTop: 20, paddingBottom: 24 }}>
          {featured.length ? (
            <>
              <div style={{ color: colors.onSurface, fontSize: 18, fontFamily: fonts.display, fontWeight: 700, padding: "0 24px" }}>🔥 This Week's Focus</div>
              <div style={{ color: colors.muted, fontSize: 13, lineHeight: "19px", padding: "4px 24px 14px", fontFamily: fonts.text }}>Not sure what to post? Start here. These are the products, launches and offers we're shouting about right now.</div>
              <div className="hide-scroll" style={{ display: "flex", gap: 12, overflowX: "auto", padding: "0 24px", marginBottom: 16 }}>
                {featured.map((f) => (
                  <div key={f.id} style={{ width: 240, height: 140, flexShrink: 0, borderRadius: 20, padding: 16, display: "flex", flexDirection: "column", justifyContent: "space-between", background: `linear-gradient(135deg, ${colors.brand}, ${colors.brandSecondary})` }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ background: "rgba(255,255,255,0.22)", borderRadius: 999, padding: "4px 10px", color: colors.onBrand, fontSize: 11, fontWeight: 700, fontFamily: fonts.text }}>{CATEGORY_LABEL[f.category]}</span>
                      {f.isNew ? <NewBadge /> : null}
                    </div>
                    <Icon name={CATEGORY_ICON[f.category]} size={26} color={colors.onBrand} style={{ opacity: 0.9 }} />
                    <div style={{ color: colors.onBrand, fontSize: 16, fontWeight: 700, lineHeight: "21px", fontFamily: fonts.text }}>{f.title}</div>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          <ShopRitzyRa />

          <div className="hide-scroll" style={{ display: "flex", gap: 8, overflowX: "auto", padding: "6px 24px 0", alignItems: "center" }}>
            {CONTENT_CATEGORIES.map((c) => {
              const active = selected === c.key;
              return (
                <button key={c.key} data-testid={`chip-${c.key}`} className="pressable" onClick={() => setSelected(c.key)} style={{ height: 36, flexShrink: 0, padding: "0 16px", borderRadius: 999, border: `1px solid ${active ? colors.brand : colors.border}`, background: active ? colors.brand : colors.surfaceSecondary, color: active ? colors.onBrand : colors.onSurfaceSecondary, fontSize: 13, fontWeight: active ? 700 : 600, cursor: "pointer", fontFamily: fonts.text }}>{c.label}</button>
              );
            })}
          </div>

          <div style={{ padding: "16px 24px 0" }}>{items.map(renderItem)}</div>

          <div data-testid="content-guidance" style={{ margin: "6px 24px 0", background: colors.brandTertiary, borderRadius: 18, padding: 18 }}>
            <div style={{ color: colors.onBrandTertiary, fontSize: 17, fontFamily: fonts.display, fontWeight: 700 }}>Make it yours 🖤</div>
            <div style={{ color: colors.onBrandTertiary, fontSize: 14, lineHeight: "21px", marginTop: 8, fontFamily: fonts.text }}>These resources are here to make content easier, not turn you into a RitzyRa copy-and-paste robot 😂 Use the photos, facts, captions and ideas, but add your own personality wherever you can. People buy from people.</div>
          </div>
          <div style={{ display: "flex", gap: 10, margin: "12px 24px 0", padding: 14, borderRadius: 14, border: `1px solid ${colors.border}`, background: colors.surfaceSecondary }}>
            <Icon name="info" size={15} color={colors.muted} />
            <div style={{ flex: 1, color: colors.muted, fontSize: 13, lineHeight: "19px", fontFamily: fonts.text }}>Only use claims and crystal information supplied or approved by RitzyRa when talking about product properties. Avoid medical or health claims.</div>
          </div>
        </div>
      )}
    </div>
  );
}
